import { useTranslation } from 'react-i18next';

import { cn } from '~/utils/cn';

import type { DeliveredFieldStatus } from '../../utils/delivery-field-keys';

/** Legend order — sent first (the affirming case), unknown last (the
 *  least actionable). */
const ORDER: DeliveredFieldStatus[] = ['sent', 'not_sent', 'truncated', 'failed', 'unknown'];

/** Pill background + text per status, at rest — the same tone→class pairs
 *  `DeliveryStatusChip`'s internal `TONE_STYLE` map uses (that map is
 *  private to its file, so this repeats the 4 pairs rather than importing
 *  a component's internals). A chip reads as a filter at a glance this way,
 *  rather than as plain text that happens to be clickable. */
const PILL_CLASS: Record<DeliveredFieldStatus, string> = {
  sent: 'bg-success-50 text-success',
  truncated: 'bg-warning-50 text-warning',
  failed: 'bg-error-50 text-error',
  not_sent: 'bg-muted text-muted-foreground',
  unknown: 'bg-muted text-muted-foreground',
};

const DOT_CLASS: Record<DeliveredFieldStatus, string> = {
  sent: 'bg-success',
  truncated: 'bg-warning',
  failed: 'bg-error',
  not_sent: 'bg-muted-foreground',
  unknown: 'bg-muted-foreground',
};

interface Props {
  counts: Partial<Record<DeliveredFieldStatus, number>>;
  /** The active filter, or `null` when every status is shown. */
  active: DeliveredFieldStatus | null;
  /** Clicking the active chip clears the filter (toggle, not a one-way select). */
  onToggle: (status: DeliveredFieldStatus) => void;
}

/**
 * Delivery-status counts for the selected product, doubling as a filter —
 * click a status to show only its attributes, click it again to clear.
 * Mirrors the existing count-as-filter legend on the Product Details
 * attribute diff (`AttributeFilterCard`): a count next to a label, not a
 * separate control. Every chip carries its status tint at rest, so the row
 * reads as a set of filters rather than plain, incidentally-clickable text;
 * the active one gets a ring, and the rest dim slightly so the current
 * selection is unambiguous.
 */
export function DeliveryStatusLegend({ counts, active, onToggle }: Props) {
  const { t } = useTranslation('catalog-operations');
  const visible = ORDER.filter((status) => (counts[status] ?? 0) > 0);
  if (visible.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      role="group"
      aria-label={t('deliveryCompare.legend.label')}
      data-testid="delivery-compare-status-filter"
    >
      {visible.map((status) => {
        const isActive = active === status;
        const isDimmed = active !== null && !isActive;
        return (
          <button
            key={status}
            type="button"
            aria-pressed={isActive}
            data-testid={`delivery-compare-status-filter-${status.replace(/_/g, '-')}`}
            onClick={() => {
              onToggle(status);
            }}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-all',
              PILL_CLASS[status],
              isActive && 'ring-2 ring-current/40',
              isDimmed && 'opacity-50',
            )}
          >
            <span className={cn('size-1.5 rounded-full', DOT_CLASS[status])} aria-hidden="true" />
            {t(`deliveryCompare.fieldStatus.${status}`)}
            <span
              className="tabular-nums"
              data-testid={`delivery-compare-status-filter-${status.replace(/_/g, '-')}-value`}
            >
              {counts[status]}
            </span>
          </button>
        );
      })}
    </div>
  );
}
